import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../../../shared/ui';

/**
 * One arrow inside the joined control. No rounding and no background of its own — the container
 * owns both, so the pair reads as a single object and a hover fills exactly one half of it.
 */
const ARROW_CLASS = [
  'flex h-11 w-11 shrink-0 items-center justify-center',
  'text-[#004494] transition duration-200',
  'hover:bg-[#004494] hover:text-white',
  'disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent disabled:hover:text-[#004494]',
].join(' ');

interface Props {
  onPrev: () => void;
  onNext: () => void;
  onToday: () => void;
  /** False once the visible range already contains today, which is when `Heute` goes quiet. */
  canGoToToday: boolean;
  /** Accessible names differ by unit — "Vorheriger Tag" against "Vorherige Woche". */
  prevLabel: string;
  nextLabel: string;
}

/**
 * Prev / next / Heute for whatever range the board is showing.
 *
 * Replaces the near-identical `DispositionDayNav` and `DispositionWeekNav`, whose own docblock
 * admitted the duplication ("Same three pills … they have to be the same height and rhythm") and
 * left that agreement to be maintained by hand. The unit now arrives as two aria labels and a
 * predicate, which is the entire difference there ever was between them.
 *
 * The two arrows are joined into one control rather than left as separate circles. Three floating
 * pills in a row read as three unrelated actions; a joined pair reads as one axis with two
 * directions, which is what it is. `DispositionToolbar` sets the label beside it, so this no longer
 * carries the date — and with it goes the `min-w-[250px] text-right` that only existed to stop
 * these buttons jumping when the label's width changed.
 */
export const DispositionRangeNav = ({
  onPrev,
  onNext,
  onToday,
  canGoToToday,
  prevLabel,
  nextLabel,
}: Props) => (
  <div className="flex items-center gap-3">
    <div className="flex items-center overflow-hidden rounded-full bg-white">
      <button type="button" onClick={onPrev} aria-label={prevLabel} className={ARROW_CLASS}>
        <ChevronLeft className="h-6 w-6" strokeWidth={2.5} aria-hidden />
      </button>
      <span className="h-6 w-px shrink-0 bg-[#004494]/15" aria-hidden />
      <button type="button" onClick={onNext} aria-label={nextLabel} className={ARROW_CLASS}>
        <ChevronRight className="h-6 w-6" strokeWidth={2.5} aria-hidden />
      </button>
    </div>

    <Button
      variant="surface"
      size="sm"
      fullWidth={false}
      onClick={onToday}
      disabled={!canGoToToday}
      className="font-['PT_Sans'] text-[18px]"
    >
      Heute
    </Button>
  </div>
);
